import { z } from 'zod'
import { optionalText, pageQuerySchema, versionSchema } from './common.js'
import { csvSafe, toCsv } from './csv.js'

/**
 * Service release C1: sites, places and assets (docs/refactor/SERVICE_CONTRACTS.md, C1_BREAKDOWN.md).
 * A customer's site holds a tree of places (Floor › Apartment, Level › Shop…), labelled by a site
 * template; each place (or the site itself) holds assets: installed equipment such as AC units, of a
 * company-defined asset type that carries its own fields and checklist. Contracts and planned visits
 * come in C2/C3 and build on this register.
 */

const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)
const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const nullableDate = z.iso
  .date()
  .nullish()
  .transform((v) => v ?? null)
const keySchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase letters, digits and underscores, starting with a letter')
const codeSchema = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .regex(/^[A-Za-z0-9_.-]+$/, 'Letters, digits, dot, dash and underscore')

/** The deepest a place tree goes under a site (Building › Floor › Unit › Room). */
export const PLACE_MAX_DEPTH = 4
/** At most this many places in one site, and in one generate. */
export const SITE_MAX_PLACES = 10_000
export const PLACE_GENERATE_MAX = 2_000
/** At most this many assets in one add-identical or copy. */
export const ASSET_BULK_MAX = 500

// ── site templates (`/service/site-templates`, service.configure) ─────────────

export const siteTemplateLevelSchema = z.object({
  /** The level's name: "Floor", "Apartment". */
  en: requiredText(60),
  ar: requiredText(60),
})
export type SiteTemplateLevel = z.infer<typeof siteTemplateLevelSchema>

export const siteTemplateInputSchema = z.object({
  code: codeSchema,
  name: requiredText(120),
  nameAr: optionalText(120),
  /** From the top: Floor, then Apartment. Empty: assets sit on the site itself (a villa, a shop). */
  levels: z.array(siteTemplateLevelSchema).max(PLACE_MAX_DEPTH),
})
export type SiteTemplateInput = z.input<typeof siteTemplateInputSchema>
export const siteTemplateUpdateSchema = siteTemplateInputSchema.extend({ version: versionSchema })

export interface SiteTemplate {
  id: string
  code: string
  name: string
  nameAr: string | null
  levels: SiteTemplateLevel[]
  archived: boolean
  /** Sites using it (a template in use keeps its number of levels). */
  siteCount: number
  version: number
}

/** The standard templates (`POST /service/site-templates/presets` adds the missing ones). */
export const SITE_TEMPLATE_PRESETS: readonly SiteTemplateInput[] = [
  {
    code: 'TOWER',
    name: 'Residential tower',
    nameAr: 'برج سكني',
    levels: [
      { en: 'Floor', ar: 'الطابق' },
      { en: 'Apartment', ar: 'الشقة' },
    ],
  },
  {
    code: 'OFFICE',
    name: 'Office building',
    nameAr: 'مبنى مكاتب',
    levels: [
      { en: 'Floor', ar: 'الطابق' },
      { en: 'Office', ar: 'المكتب' },
    ],
  },
  {
    code: 'HOTEL',
    name: 'Hotel',
    nameAr: 'فندق',
    levels: [
      { en: 'Floor', ar: 'الطابق' },
      { en: 'Room', ar: 'الغرفة' },
    ],
  },
  {
    code: 'MALL',
    name: 'Mall',
    nameAr: 'مجمع تجاري',
    levels: [
      { en: 'Level', ar: 'المستوى' },
      { en: 'Shop or area', ar: 'المحل أو المنطقة' },
    ],
  },
  {
    code: 'COMPOUND',
    name: 'Villa compound',
    nameAr: 'مجمع فلل',
    levels: [
      { en: 'Villa', ar: 'الفيلا' },
      { en: 'Room', ar: 'الغرفة' },
    ],
  },
  {
    code: 'FACTORY',
    name: 'Factory or warehouse',
    nameAr: 'مصنع أو مستودع',
    levels: [
      { en: 'Hall', ar: 'الصالة' },
      { en: 'Zone', ar: 'المنطقة' },
    ],
  },
  { code: 'SINGLE', name: 'Villa or shop', nameAr: 'فيلا أو محل', levels: [] },
]

// ── places (`/service/sites/:siteId/places`) ─────────────────────────────────

const placeFields = z.object({
  /** "12", "1204", "Lobby", "Roof plant room". Unique among its siblings. */
  name: requiredText(80),
  nameAr: optionalText(80),
  /** Who lives or works there, for access (personal data: kept off reports and exports). */
  occupantName: optionalText(120),
  occupantPhone: optionalText(40),
  accessNotes: optionalText(500),
})
/** `POST /service/sites/:siteId/places` (service.assets): under `parentId` (null: the site's top level). */
export const placeInputSchema = placeFields.extend({ parentId: nullableUuid })
export type PlaceInput = z.input<typeof placeInputSchema>
export const placeUpdateSchema = placeFields.extend({ version: versionSchema })
export type PlaceUpdate = z.input<typeof placeUpdateSchema>
/** `POST …/places/:placeId/move {parentId, version}`: within the same site, never under its own subtree. */
export const placeMoveSchema = z.object({ parentId: nullableUuid, version: versionSchema })
/** `POST …/places/:placeId/archive {version}`: with its subtree; refused while assets IN_SERVICE sit there. */
export const placeArchiveSchema = z.object({ version: versionSchema })

/**
 * `POST /service/sites/:siteId/places/generate` (and `/generate/preview`): makes many places in one step,
 * e.g. floors B1, G, 1–20, each with apartments 01–08 named "{parent}{n}" (1201, 1202…).
 */
export const placeGenerateSchema = z
  .object({
    /** Under this place (null: the site's top level). */
    parentId: nullableUuid,
    /** The places to make at this level: explicit names, plus a numbered range. */
    names: z.array(requiredText(80)).max(200).default([]),
    range: z
      .object({
        from: z.number().int().min(-20).max(500),
        to: z.number().int().min(-20).max(500),
        /** Digits: 2 makes 01, 02… */
        pad: z.number().int().min(1).max(4).default(1),
        prefix: z.string().max(20).default(''),
        suffix: z.string().max(20).default(''),
      })
      .nullish()
      .transform((v) => v ?? null),
    /** Children of each place made: `count` of them named by `pattern` ({parent} and {n}, padded to `pad`). */
    children: z
      .object({
        count: z.number().int().min(1).max(200),
        pattern: z.string().trim().min(1).max(40).default('{parent}{n}'),
        pad: z.number().int().min(1).max(4).default(2),
      })
      .nullish()
      .transform((v) => v ?? null),
  })
  .refine((v) => v.names.length > 0 || v.range !== null, {
    path: ['names'],
    message: 'Name the places or give a range',
  })
  .refine((v) => v.range === null || v.range.to >= v.range.from, {
    path: ['range'],
    message: 'The range ends before it starts',
  })
export type PlaceGenerateInput = z.input<typeof placeGenerateSchema>
export interface PlaceGeneratePreview {
  /** The places that would be made, in order (children under their parent), and those that already exist. */
  places: { name: string; parentName: string | null; exists: boolean }[]
  create: number
  existing: number
}

/** At most this many assets in one duplicate (its places count against PLACE_GENERATE_MAX). */
export const PLACE_DUPLICATE_ASSETS_MAX = 2_000

/**
 * `POST /service/sites/:siteId/places/:placeId/duplicate` (and `/duplicate/preview`): copies a place's
 * layout, the places inside it at every depth, and the assets in service there (type, brand, model,
 * position and fields, with new tags; no serials, dates, notes, occupants or access notes). The copies are
 * new places next to it, named as the generator names them (`names`, `range`), or places of its level that
 * exist (`targetPlaceIds`). A copy's place that exists already (the same name, any case) is reused, and a
 * place that holds assets of its own gets none, so a repeat makes nothing.
 */
export const placeDuplicateSchema = z
  .object({
    names: z.array(requiredText(80)).max(200).default([]),
    range: placeGenerateSchema.shape.range,
    targetPlaceIds: z.array(z.uuid()).max(PLACE_GENERATE_MAX).default([]),
    /** The places inside it (false: the copies only). */
    places: z.boolean().default(true),
    /** The assets in service in it and in the places inside it. */
    assets: z.boolean().default(true),
    /** A name inside that starts with the place's name starts with the copy's name instead: 101 → 201. */
    renumber: z.boolean().default(false),
  })
  .refine((v) => v.names.length > 0 || v.range !== null || v.targetPlaceIds.length > 0, {
    path: ['names'],
    message: 'Name the copies or choose the places to copy into',
  })
  .refine((v) => v.targetPlaceIds.length === 0 || (v.names.length === 0 && v.range === null), {
    path: ['targetPlaceIds'],
    message: 'Copy as new places or into existing ones, not both',
  })
  .refine((v) => v.range === null || v.range.to >= v.range.from, {
    path: ['range'],
    message: 'The range ends before it starts',
  })
export type PlaceDuplicateInput = z.input<typeof placeDuplicateSchema>
export interface PlaceDuplicatePreview {
  /**
   * The copies' places in outline order (each copy, then the places inside it), the first 200: the path from
   * the copy down and whether the place is there already.
   */
  places: { path: string[]; exists: boolean }[]
  /** The copies themselves: new, and already there (targets, or a name used next to it). */
  copies: { create: number; existing: number }
  /** Every place: made, and already there. */
  create: number
  existing: number
  /** Assets made, and places that keep their own assets (none copied there). */
  assets: number
  skipped: number
}

/**
 * One step of a place's path, from the top. Screens show a path from its steps in the reader's language
 * (web `PlacePath`); the plain `path` strings are English, for files and search.
 */
export interface PlaceStep {
  id: string
  name: string
  nameAr: string | null
}

export interface PlaceNode {
  id: string
  parentId: string | null
  /** 1 = the site's top level. */
  depth: number
  /** The site template's level name at this depth ("Floor"), or null without a template. */
  level: { en: string; ar: string } | null
  name: string
  nameAr: string | null
  /** "12 › 1204", English names (files and search). Screens build the path from the tree (`parentId`). */
  path: string
  sort: number
  /** Only for readers who may see personal data (service.assets); otherwise null. */
  occupantName: string | null
  occupantPhone: string | null
  accessNotes: string | null
  /** Assets here and below, and how many are out of order. */
  assetCount: number
  issueCount: number
  archived: boolean
  version: number
}
/** `GET /service/sites/:siteId/places?archived=`: the whole tree, flat, in outline order. */
export interface SitePlaces {
  siteId: string
  template: {
    id: string
    code: string
    name: string
    nameAr: string | null
    levels: SiteTemplateLevel[]
  } | null
  places: PlaceNode[]
}

// ── asset types (`/service/asset-types`, service.configure) ──────────────────

export const ASSET_FIELD_KINDS = ['text', 'number', 'select', 'date', 'boolean'] as const
export type AssetFieldKind = (typeof ASSET_FIELD_KINDS)[number]
export const assetFieldSchema = z
  .object({
    key: keySchema,
    en: requiredText(60),
    ar: requiredText(60),
    kind: z.enum(ASSET_FIELD_KINDS),
    /** "BTU", "kg", "kW": shown after the value. */
    unit: optionalText(20),
    options: z
      .array(
        z
          .object({ value: z.string().trim().min(1).max(40), en: optionalText(60), ar: optionalText(60) })
          // A label left empty reads as the value ("R410A" is typed once).
          .transform((o) => ({ value: o.value, en: o.en ?? o.value, ar: o.ar ?? o.value })),
      )
      .max(50)
      .default([]),
    required: z.boolean().default(false),
  })
  .refine((f) => f.kind !== 'select' || f.options.length > 0, {
    path: ['options'],
    message: 'A choice field needs its options',
  })
export type AssetField = z.output<typeof assetFieldSchema>

export const CHECK_ITEM_KINDS = ['check', 'reading'] as const
export const checkItemSchema = z.object({
  key: keySchema,
  en: requiredText(120),
  ar: requiredText(120),
  /** check: done or not; reading: a number to record (gas pressure, current). */
  kind: z.enum(CHECK_ITEM_KINDS),
  unit: optionalText(20),
})
export type CheckItem = z.output<typeof checkItemSchema>

const uniqueKeys = (list: { key: string }[]) => new Set(list.map((x) => x.key)).size === list.length
export const assetTypeInputSchema = z.object({
  code: codeSchema,
  name: requiredText(120),
  nameAr: optionalText(120),
  /** Tags of new assets without one: "AC" makes AC-00001. 2–6 capital letters. */
  tagPrefix: z
    .string()
    .trim()
    .regex(/^[A-Z]{2,6}$/, 'Two to six capital letters'),
  fields: z.array(assetFieldSchema).max(30).default([]).refine(uniqueKeys, 'Each field needs its own key'),
  /** Used by visits from C3. */
  checklist: z.array(checkItemSchema).max(40).default([]).refine(uniqueKeys, 'Each item needs its own key'),
  /** The usual service interval in months (plans default to it from C3). */
  intervalMonths: z
    .number()
    .int()
    .min(1)
    .max(60)
    .nullish()
    .transform((v) => v ?? null),
})
export type AssetTypeInput = z.input<typeof assetTypeInputSchema>
export const assetTypeUpdateSchema = assetTypeInputSchema.extend({ version: versionSchema })

export interface AssetType {
  id: string
  code: string
  name: string
  nameAr: string | null
  tagPrefix: string
  fields: AssetField[]
  checklist: CheckItem[]
  intervalMonths: number | null
  archived: boolean
  assetCount: number
  version: number
}

export const ASSET_TYPE_INDUSTRIES = [
  'HVAC',
  'LIFTS',
  'FIRE',
  'POWER',
  'PLUMBING',
  'PEST',
  'SECURITY',
] as const
export type AssetTypeIndustry = (typeof ASSET_TYPE_INDUSTRIES)[number]
/** `POST /service/asset-types/presets {industries}`: adds the standard types of those industries that are missing. */
export const assetTypePresetsSchema = z.object({
  industries: z.array(z.enum(ASSET_TYPE_INDUSTRIES)).min(1),
})

// ── assets (`/service/assets`) ───────────────────────────────────────────────

export const ASSET_CONDITIONS = ['GOOD', 'FAIR', 'POOR'] as const
export type AssetCondition = (typeof ASSET_CONDITIONS)[number]
/** REPLACED and REMOVED are final; OUT_OF_ORDER goes back to IN_SERVICE. */
export const ASSET_STATUSES = ['IN_SERVICE', 'OUT_OF_ORDER', 'REMOVED', 'REPLACED'] as const
export type AssetStatus = (typeof ASSET_STATUSES)[number]

/** A type field's value: text, a number as a string, a choice's value, a date, a boolean. */
export type AssetFieldValue = string | boolean | null
const assetValues = z.record(keySchema, z.union([z.string().trim().max(200), z.boolean(), z.null()]))

const assetFields = z.object({
  /** In this site; null: on the site itself (a rooftop chiller, a villa's AC). */
  placeId: nullableUuid,
  typeId: z.uuid(),
  /** Unique in the company; omitted: the type's prefix and the next number (AC-00001). */
  tag: z
    .string()
    .trim()
    .max(40)
    .regex(/^[A-Za-z0-9_.\-/]+$/, 'Letters, digits and . _ - /')
    .nullish()
    .transform((v) => (v ? v : null)),
  /** Which one it is where several share a place: "Bedroom 2", "Living room". */
  position: optionalText(60),
  brand: optionalText(80),
  model: optionalText(80),
  serial: optionalText(100),
  /** The type's fields, by key; checked against the type (required, number, choice, date). */
  values: assetValues.default({}),
  installedOn: nullableDate,
  warrantyUntil: nullableDate,
  condition: z
    .enum(ASSET_CONDITIONS)
    .nullish()
    .transform((v) => v ?? null),
  /** Optional links: the catalogue model, and the serial when it was sold from our stock. */
  variantId: nullableUuid,
  productSerialId: nullableUuid,
  notes: optionalText(1000),
})
/** `POST /service/sites/:siteId/assets` (service.assets). */
export const assetInputSchema = assetFields
export type AssetInput = z.input<typeof assetInputSchema>
export const assetUpdateSchema = assetFields.extend({ version: versionSchema })
export type AssetUpdate = z.input<typeof assetUpdateSchema>

/** `POST /service/sites/:siteId/assets/bulk`: `count` identical assets (tags numbered), at one place. */
export const assetBulkSchema = assetFields.omit({ tag: true, serial: true, productSerialId: true }).extend({
  count: z.number().int().min(1).max(ASSET_BULK_MAX),
})
export type AssetBulkInput = z.input<typeof assetBulkSchema>
/**
 * `POST /service/places/:placeId/copy-assets {targetPlaceIds}`: copies the place's IN_SERVICE assets to each
 * target (same site): type, brand, model and fields; new tags; no serial, dates or links.
 */
export const assetCopySchema = z.object({
  targetPlaceIds: z.array(z.uuid()).min(1).max(ASSET_BULK_MAX),
})
export interface AssetBulkResult {
  created: number
  assetIds: string[]
}

/** `POST /service/assets/:id/status {status, note, version}`: OUT_OF_ORDER ↔ IN_SERVICE, or REMOVED. */
export const assetStatusSchema = z.object({
  status: z.enum(['IN_SERVICE', 'OUT_OF_ORDER', 'REMOVED']),
  note: optionalText(500),
  version: versionSchema,
})
/**
 * `POST /service/assets/:id/replace`: the old asset becomes REPLACED and points to the new one, made at the
 * same place with the given fields (history stays on each).
 */
export const assetReplaceSchema = assetFields.omit({ placeId: true }).extend({
  version: versionSchema,
  note: optionalText(500),
})

export interface AssetRef {
  id: string
  tag: string
  type: { id: string; code: string; name: string; nameAr: string | null }
  /** "Bedroom 2": tells apart several of a type in one place. */
  position: string | null
  /** "12 › 1204" in English (files and search), or null on the site itself. Screens show `placeSteps`. */
  placePath: string | null
  /** The place's path from the top; empty on the site itself. */
  placeSteps: PlaceStep[]
  status: AssetStatus
}

export interface Asset extends AssetRef {
  site: {
    id: string
    name: string
    customerId: string
    customer: {
      id: string
      code: string
      name: string
      nameAr: string | null
      /** Only for readers with customers.read; otherwise null. */
      mobile: string | null
    }
  }
  placeId: string | null
  brand: string | null
  model: string | null
  serial: string | null
  values: Record<string, AssetFieldValue>
  installedOn: string | null
  warrantyUntil: string | null
  /** Warranty still running today. */
  underWarranty: boolean
  condition: AssetCondition | null
  variant: { id: string; sku: string; name: string } | null
  productSerialId: string | null
  notes: string | null
  replacedBy: { id: string; tag: string } | null
  replaces: { id: string; tag: string } | null
  /** The last visit that did work on it (outcome FULL or PARTIAL), not a job that is only planned. */
  lastService: { visitId: string; jobId: string; number: string; date: string } | null
  /** The newest job still open on it (the "Report fault" warning), and how many are open. */
  openJob: { id: string; number: string; status: string } | null
  openJobs: number
  /** The note of its latest status change ("Not cooling, water leak"): prefills a fault report. */
  statusNote: string | null
  can: { edit: boolean; changeStatus: boolean; replace: boolean }
  version: number
}

export const ASSET_SORT_FIELDS = ['tag', 'type', 'place', 'installedOn', 'status'] as const
/**
 * `GET /service/sites/:siteId/assets?placeId&below&typeId&status&final&q`: `below` includes the place's
 * subtree. `q` matches the tag, position, brand, model, serial and the place's path in either language
 * ("1204" finds every asset in or under 1204). REPLACED and REMOVED assets show only with `final` (or when
 * `status` names one), so the list counts what the site's header counts.
 */
export const assetListQuerySchema = pageQuerySchema.extend({
  placeId: z.uuid().optional(),
  below: z.stringbool().optional(),
  typeId: z.uuid().optional(),
  status: z.enum(ASSET_STATUSES).optional(),
  final: z.stringbool().optional(),
})
export type AssetListQuery = z.input<typeof assetListQuerySchema>

/** The cross-site asset list's sorts; "-" first sorts descending. */
export const ASSET_REGISTER_SORTS = [
  'site',
  '-site',
  'tag',
  '-tag',
  'type',
  '-type',
  'installedOn',
  '-installedOn',
  'status',
  '-status',
] as const
export type AssetRegisterSort = (typeof ASSET_REGISTER_SORTS)[number]
/**
 * `GET /service/assets?q&typeId&status&final&sort&page&pageSize` (service.read): the assets of every site
 * in the sites register (archived sites left out), paged. `q` matches the tag, position, brand, model and
 * serial, the site's name and code, and its customer's name and code. REPLACED and REMOVED show only with
 * `final` or when `status` names one, as on a site. By site, then tag, unless sorted otherwise.
 */
export const assetRegisterQuerySchema = pageQuerySchema.extend({
  typeId: z.uuid().optional(),
  status: z.enum(ASSET_STATUSES).optional(),
  final: z.stringbool().optional(),
  sort: z.enum(ASSET_REGISTER_SORTS).default('site'),
})
export type AssetRegisterQuery = z.input<typeof assetRegisterQuerySchema>

/** `GET /service/assets/lookup?tag=`: an asset by its tag (the scanner path), 404 when none. */
export const assetLookupQuerySchema = z.object({ tag: z.string().trim().min(1).max(40) })

/** `GET /service/assets/:id/history`: what happened to it, newest first. */
export interface AssetHistoryEntry {
  /** C3: CHECK is this asset's own check on a visit (outcome, readings, note). */
  kind: 'JOB' | 'VISIT' | 'STATUS' | 'REPLACED' | 'CREATED' | 'CHECK'
  at: string
  /** CHECK only (optional for older fixtures). */
  check?: {
    outcome: 'DONE' | 'ISSUE' | 'NO_ACCESS' | 'SKIPPED'
    results: Record<string, boolean | string>
    reason: string | null
    faultJob: { id: string; number: string } | null
    /** The checklist the results answer (labels and units of their keys); optional for older fixtures. */
    items?: CheckItem[]
  } | null
  job: { id: string; number: string; status: string } | null
  visit: {
    id: string
    date: string
    outcome: 'FULL' | 'PARTIAL' | 'NONE' | null
    workPerformed: string | null
    findings: string | null
  } | null
  /** STATUS: the new status and the note. */
  status: AssetStatus | null
  note: string | null
  by: { id: string; name: string; nameAr: string | null } | null
}

// ── import (`/service/sites/:siteId/import/{preview,apply}`, service.assets) ──

/**
 * The CSV columns. `place` is the path from the top, separated by " / " or " › " ("12 / 1204", as copied from
 * a screen); missing places are made and listed in the preview. A row with a `tag` that exists updates that asset; otherwise it makes one. Type fields are columns
 * named `f:<key>` (e.g. `f:capacity`).
 */
export const ASSET_IMPORT_COLUMNS = [
  'place',
  'type',
  'tag',
  'position',
  'brand',
  'model',
  'serial',
  'installed_on',
  'warranty_until',
  'condition',
  'notes',
] as const
export const ASSET_IMPORT_MAX_ROWS = 20_000
export const assetImportPreviewSchema = z.object({ fileId: z.uuid() })
export const assetImportApplySchema = z.object({
  fileId: z.uuid(),
  fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
})
export const ASSET_IMPORT_ACTIONS = ['CREATE', 'UPDATE', 'UNCHANGED', 'ERROR'] as const
export interface AssetImportPlan {
  fingerprint: string
  rows: {
    line: number
    action: (typeof ASSET_IMPORT_ACTIONS)[number]
    tag: string | null
    place: string | null
    errors: { column: string; message: string }[]
  }[]
  counts: { create: number; update: number; unchanged: number; error: number; newPlaces: number }
  /** The places the import will make, as English paths ("12 / 1205"), the first 200. */
  newPlacePaths: string[]
  fileErrors: string[]
}

// ── the assistant's reading of an equipment list (SA1, SERVICE_ASSISTANT.md §3.4) ─

/** At most this many rows in one reading, each for at most this many identical assets. */
export const SITE_CAPTURE_MAX_ROWS = 300
export const SITE_CAPTURE_MAX_COUNT = 50

/**
 * What `service.capture` read from a typed list or a document: equipment rows for the site's CSV import. Strict
 * for the model (every field present, nullable): the place path as the site names it ("Villa 1 / Kitchen"; null:
 * the site itself), the asset type's code, how many identical ones, and what is written of them. The import's
 * rules check it all; dates are copied as read and the import refuses one that is not YYYY-MM-DD.
 */
export const serviceSiteCaptureSchema = z.object({
  rows: z
    .array(
      z.object({
        place: z.string().max(400).nullable(),
        type: z.string().max(120),
        count: z.number().int().min(1).max(SITE_CAPTURE_MAX_COUNT),
        position: z.string().max(80).nullable(),
        brand: z.string().max(80).nullable(),
        model: z.string().max(80).nullable(),
        serial: z.string().max(80).nullable(),
        installedOn: z.string().max(20).nullable(),
        warrantyUntil: z.string().max(20).nullable(),
        condition: z.enum(ASSET_CONDITIONS).nullable(),
        notes: z.string().max(500).nullable(),
        /** The type's fields as written, by the field's key ("capacity" → "18000"). */
        values: z.array(z.object({ key: z.string().max(40), value: z.string().max(200) })).max(20),
        confidence: z.number().min(0).max(1),
      }),
    )
    .max(SITE_CAPTURE_MAX_ROWS),
  /** The document had more rows than one reading holds. */
  truncated: z.boolean(),
  /** What could not be read or was unsure, in the request's language. */
  notes: z.array(z.string().max(300)).max(10),
})
export type ServiceSiteCapture = z.infer<typeof serviceSiteCaptureSchema>

/**
 * A reading as the site's CSV import file: one line per asset (a row's `count` repeats it; a serial number stays
 * on the first only), the import's columns, then an `f:<key>` column per field read. The import's preview checks
 * it like a file the user picked.
 */
export function siteCaptureToCsv(capture: ServiceSiteCapture): string {
  const keys = [...new Set(capture.rows.flatMap((r) => r.values.map((v) => v.key.trim()).filter(Boolean)))]
  const header = [
    'place',
    'type',
    'position',
    'brand',
    'model',
    'serial',
    'installed_on',
    'warranty_until',
    'condition',
    'notes',
    ...keys.map((k) => `f:${k}`),
  ]
  const lines: (string | null)[][] = [header]
  for (const row of capture.rows) {
    const values = new Map(row.values.map((v) => [v.key.trim(), v.value]))
    for (let i = 0; i < row.count; i++) {
      // Read from a document, not typed as a file: no cell may start a spreadsheet formula.
      lines.push(
        [
          row.place,
          row.type,
          row.position,
          row.brand,
          row.model,
          i === 0 ? row.serial : null,
          row.installedOn,
          row.warrantyUntil,
          row.condition,
          row.notes,
          ...keys.map((k) => values.get(k) ?? null),
        ].map(csvSafe),
      )
    }
  }
  return toCsv(lines)
}

// ── errors ───────────────────────────────────────────────────────────────────

export const SERVICE_ASSET_ERROR_CODES = [
  /** 409: the code or tag is taken. */
  'CODE_TAKEN',
  'TAG_TAKEN',
  /** 422: deeper than the template's levels (or PLACE_MAX_DEPTH), or over the site's limits. */
  'PLACE_TOO_DEEP',
  'SITE_LIMIT',
  /** 409: a sibling already has that name. */
  'PLACE_NAME_TAKEN',
  /** 422: a place moved under itself or its subtree. */
  'PLACE_CYCLE',
  /** 422: the place or asset is of another site. */
  'NOT_OF_SITE',
  /** 422: archiving a place that still holds assets in service. */
  'PLACE_HAS_ASSETS',
  /** 422: a REPLACED or REMOVED asset doesn't change. */
  'ASSET_FINAL',
  /** 422: a template in use can't change its number of levels. */
  'TEMPLATE_IN_USE',
  /** 409: the import file changed or the site changed since the preview. */
  'IMPORT_STALE',
  /** 409: the asset register is switched off (Settings › Service). */
  'ASSETS_OFF',
] as const
export type ServiceAssetErrorCode = (typeof SERVICE_ASSET_ERROR_CODES)[number]
